<script lang="ts" setup>
/**
 * トークルーム投稿 > メール通知送付。
 */

import { CommunityTalkStore, isValidCommunityTalkStoreKey } from "@/store/communityTalk/communityTalkStore";
import { defineProps, inject, reactive, toRefs } from "vue";
import ApiPostLoadMaster from "@/components/common/api/ApiPostLoadMaster.vue";
import PostComplete from "@/components/communityTalk/PostComplete.vue";
import { ApiPostLoadStatus, ApiPostLoadStatusType } from "@/modules/types/apiLoadStatusType";
import MailReceiveController from "@/controllers/communityTalk/mailReceiveController";
import { ApiErrorViewModel, ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import CustomInput from "@/components/common/form/CustomInput.vue";
import LoginUtil from "@/modules/utils/loginUtil";

interface State {
	/**
	 * 通知先メールアドレス。
	 */
	mailAddress: string;
	/**
	 * 通知先メールアドレス送付時のAPIステータス。
	 */
	status: ApiPostLoadStatusType;
	/**
	 * APIバリデーションエラーオブジェクト。
	 */
	apiValidationErrorViewModel: ApiValidationErrorViewModel | null;
	/**
	 * フォーム多重送信制御用フラグ
	 */
	isProcessing: boolean;
}
// reactiveな変数宣言
const state = reactive<State>({
	mailAddress: "",
	status: ApiPostLoadStatus.beforeRequest,
	apiValidationErrorViewModel: null,
	isProcessing: false,
});
const { mailAddress, status, apiValidationErrorViewModel, isProcessing } = toRefs(state);

interface Props {
	/**
	 * CommunityTalkStoreのInject用キー。
	 */
	communityTalkStoreKey: string;
	/**
	 * トークルームID。
	 */
	talkRoomId: string;
}
const props = defineProps<Props>();

// Storeの初期化処理
if (!isValidCommunityTalkStoreKey(props.communityTalkStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.communityTalkStoreKey.toString()}`);
}
const communityTalkStore = inject(props.communityTalkStoreKey) as CommunityTalkStore;

const mailReceiveController = new MailReceiveController();

const wordSettings = communityTalkStore.getWordSettings();

/**
 * ログイン状態でないと投稿不可かどうかのフラグ。
 * (現状、通知メール設定はトークルーム投稿時のみ行えるため、ここではトークルームの権限設定を参照する)
 */
const thisPostPermissionType = communityTalkStore.getGeneralSettings().talkRoomPostPermissionSetting;

/**
 * 通知先メールアドレス送付。
 */
const post = async (): Promise<void> => {
	state.isProcessing = true;
	// 入力欄が空であれば何もしない
	if (state.mailAddress === "") {
		state.isProcessing = false;
		return;
	}

	state.status = ApiPostLoadStatus.loading;
	try {
		// SiteUserIdの送信可否を確認
		// 不可である場合、undefindを送信する。
		let siteUserId = communityTalkStore.getSiteUserId();
		if (!LoginUtil.canSendSiteUserId(siteUserId, communityTalkStore.getGeneralSettings().nonMemberIdList, thisPostPermissionType)) {
			siteUserId = undefined;
		}

		// APIリクエスト
		await mailReceiveController.postNoticeTalkRoom(props.talkRoomId, state.mailAddress, communityTalkStore.getTenantId(), communityTalkStore.getReviCoCookieId(), siteUserId);
		state.status = ApiPostLoadStatus.success;
	} catch (e) {
		// バリデーションエラーであればエラーをを表示させ、もう1度入力できるようにする
		if ((e as ApiErrorViewModel).isValidationError) {
			state.apiValidationErrorViewModel = (e as ApiErrorViewModel).validationErrorContents;
			state.status = ApiPostLoadStatus.beforeRequest;
		} else {
			// そうでなければエラー扱い(入力はもう一度出来ない)
			state.status = ApiPostLoadStatus.error;
		}
	}
	state.isProcessing = false;
};
</script>

<template>
	<api-post-load-master :status="status">
		<template #beforeRequest>
			<section class="revico-community-talk-notice-mail-form">
				<h1 class="revico-community-talk-notice-mail-form-header">{{ wordSettings?.mailNotificationHeader }}</h1>
				<div class="revico-community-talk-notice-mail-form-wrapper">
					<p class="revico-community-talk-notice-mail-form-description">{{ wordSettings?.mailNotificationDescription }}</p>

					<custom-input
						v-model="mailAddress"
						name="mailAddress"
						type="email"
						:placeholder="wordSettings?.mailNotificationEmailPlaceholder"
						customClass="revico-community-talk-notice-mail-form-input-mailaddress"
						:api-validation-error-view-model="apiValidationErrorViewModel"
						:maxlength="256"
					/>
					<p class="revico-community-talk-notice-mail-form-notice">{{ wordSettings?.mailNotificationAnnotationText }}</p>

					<div class="revico-community-talk-notice-mail-form-submit-button-wrapper">
						<button type="button" class="revico-community-talk-notice-mail-form-submit-button" @click="post" :disabled="isProcessing">
							{{ wordSettings?.mailNotificationSendButtonLabel }}<font-awesome-icon icon="fa-solid fa-paper-plane" />
						</button>
					</div>
				</div>
			</section>
		</template>
		<template #afterApiSuccess>
			<section class="revico-community-talk-notice-mail-complete">
				<post-complete>
					<template #contents> <p class="revico-community-talk-notice-mail-complete-text">メール通知がONになりました。</p> </template>
				</post-complete>
			</section>
		</template>
	</api-post-load-master>
</template>

<style lang="scss">
.revico-widget .revico-community-talk {
	.revico-community-talk-notice-mail {
		&-form,
		&-complete {
			width: 90%;
			margin: 24px auto;
		}

		&-form {
			background-color: var(--mail-notice-bg-color);

			&-header {
				padding: 8px 20px;
				color: #fff;
				text-align: center;
				background-color: var(--accent-color);
				width: 100%;
			}

			&-wrapper {
				padding: 24px 12px;
			}
			&-description {
				margin-bottom: 12px;
				font-size: 0.8em;
				white-space: pre-line;
			}
			&-input-mailaddress {
				border: solid 1px var(--base-border-color-with-bg-white);
			}
			&-notice {
				margin-top: 12px;
				font-size: 0.8em;
				white-space: pre-line;
			}
			&-submit-button-wrapper {
				width: 100%;
				text-align: right;
			}
			&-submit-button {
				@include communitytalk_postButton();
				@include disabledInput;
				.fa-paper-plane {
					margin-left: 4px;
				}
			}
		}

		&-complete {
			text-align: center;
		}
	}
}
</style>
